import { toast } from "react-toastify";
import { endpoints, oids } from "../../../configs/Settings";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useAuth } from "../../../utils/useAuth";
import { useAxios } from "../../../utils/useAxios";
import { logger } from "../../../utils/logger";

interface IProps {
  objData: fhir5.PaymentNotice | undefined;
}

interface PaymentNoticeReportParams {
  organizationId: string;
  startDate?: string;
  endDate?: string;
  practitionerId?: string;
  locationId?: string;
  pageSize?: number; // Número de registros por página (10, 20, 30)
  pageUrl?: string; // URL para buscar página específica (usado na paginação)
  paymentStatus?: string; // Status do pagamento (opcional)
}

/**
 * Obtém um relatório de avisos de pagamento com base nos parâmetros fornecidos
 * @param params Parâmetros para filtrar o relatório
 * @returns Objeto com os dados do relatório e status da resposta
 */
export async function GetPaymentNoticeReport(
  params: PaymentNoticeReportParams
) {
  try {
    // Se temos uma URL de paginação, usamos ela diretamente
    if (params.pageUrl) {
      // Verifica se a URL começa com / ou ? para formatar corretamente
      let fullUrl = params.pageUrl;

      // Se começa com ?, precisamos adicionar o endpoint base e garantir que PaymentNotice esteja presente
      if (params.pageUrl.startsWith("?")) {
        fullUrl = `${endpoints.STATISTICS}/PaymentNotice${params.pageUrl}`;
      }
      // Se começa com /, verifica se já contém PaymentNotice
      else if (params.pageUrl.startsWith("/")) {
        // Verifica se a URL já contém PaymentNotice
        if (!params.pageUrl.includes("/PaymentNotice")) {
          // Se não contiver, adiciona PaymentNotice antes dos parâmetros
          if (params.pageUrl.includes("?")) {
            const parts = params.pageUrl.split("?");
            fullUrl = `${endpoints.STATISTICS}/PaymentNotice?${parts[1]}`;
          } else {
            fullUrl = `${endpoints.STATISTICS}/PaymentNotice${params.pageUrl}`;
          }
        } else {
          // Já contém PaymentNotice, usar a URL como está
          fullUrl = `${endpoints.STATISTICS}${params.pageUrl}`;
        }
      }
      // Se for uma URL completa, verifica se tem PaymentNotice
      else if (!params.pageUrl.includes("/PaymentNotice")) {
        // Se não for um dos casos acima e não contiver PaymentNotice, adiciona
        fullUrl = `${endpoints.STATISTICS}/PaymentNotice${
          params.pageUrl.startsWith("/") ? "" : "/"
        }${params.pageUrl}`;
      }

      logger.info(`Buscando página com URL: ${fullUrl}`);

      const response = await useAxios.get(fullUrl, {
        headers: {
          Accept: "application/fhir+json",
          Authorization: await useAuth.getUserToken(),
        },
      });

      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(`Erro ao buscar página: ${response.status}`);
      }

      return {
        data: response.data,
        status: response.status,
        links: response.data.link,
      };
    }

    // URL base para buscar os avisos de pagamento
    let url = `${endpoints.STATISTICS}/PaymentNotice/?_sort=-_lastUpdated&_include=PaymentNotice:response:Patient&_include=PaymentNotice:request:Slot&_include=Slot:schedule:Appointment&_total=accurate`;

    // Adicionar paymentStatus apenas se fornecido
    if (params.paymentStatus) {
      if (params.paymentStatus === "missing") {
        // Para buscar registros sem paymentStatus ou com status cancelled/active
        url += `&payment-status:missing=true&status=cancelled,active`;
      } else {
        url += `&payment-status=${params.paymentStatus}`;
      }
    }

    // Adicionar parâmetro de contagem para paginação
    if (params.pageSize) {
      url += `&_count=${params.pageSize}`;
    } else {
      // Padrão de 10 registros por página
      url += `&_count=10`;
    }

    // Adicionar parâmetros de filtro se fornecidos
    if (params.practitionerId && params.practitionerId !== "all") {
      // Extrair apenas o CPF do profissional, que é a última parte do ID
      // Exemplo: De "05490544000100-2.16.840.1.113883.13.237-06610657157" para "06610657157"
      const cpf = params.practitionerId.split("-").pop();

      // Usar o formato correto: Practitioner/CPF ou com o OID se necessário
      // Se o sistema requer o OID como prefixo, usar esse formato:
      url += `&reporter=Practitioner/${oids.cpf}-${cpf}`;
    }

    // Adicionar sempre o parâmetro de organização usando o oids.cnpj
    url += `&identifier=Organization/${oids.cnpj}-${params.organizationId}`;

    // Adicionar localização se fornecida
    if (params.locationId && params.locationId !== "all") {
      url += `&identifier=Location/${params.locationId}`;
    }

    if (params.startDate) {
      url += `&created=ge${params.startDate}`;
    }

    if (params.endDate) {
      url += `&created=le${params.endDate}`;
    }

    // Log da URL construída
    logger.info(`URL da requisição: ${url}`);

    // Configurar cabeçalhos
    const headers = {
      Authorization: await useAuth.getUserToken(),
      "Content-Type": "application/fhir+json",
      Accept: "application/fhir+json",
    };

    // Enviar a requisição
    const response = await useAxios.get(url, { headers, timeout: 30000 });

    // Log da resposta
    logger.info(`Resposta da API: ${JSON.stringify(response.data)}`);

    if (response.status !== HTTPResponseStatus.Success) {
      throw new Error(`Erro ao buscar PaymentNotice: ${response.status}`);
    }

    // Garantir que os links de paginação estejam presentes
    const links = response.data.link || [];
    const hasNextPage = links.some((link: any) => link.relation === "next");
    const hasPrevPage = links.some((link: any) => link.relation === "previous");

    // Se não houver links de paginação, criar com base no total e pageSize
    if (!hasNextPage && !hasPrevPage && response.data.total > 0) {
      const total = response.data.total;
      const pageSize = params.pageSize || 10;
      const currentPage = 1;

      if (total > pageSize) {
        links.push({
          relation: "next",
          url: `${url}&_getpagesoffset=${pageSize}`,
        });
      }
    }

    return {
      data: response.data,
      status: response.status,
      links: links,
    };
  } catch (error: any) {
    logger.error(
      `GetPaymentNoticeReport: ${error?.message || "Erro desconhecido"}`,
      error
    );
    // toast.error(
    //   `Erro ao obter relatório de pagamentos: ${
    //     error?.response?.status || "Falha na conexão"
    //   }`
    // );
    throw error;
  }
}
